import { getInformationWithDataV3 } from './CABE'
import { getValueCookie } from './cookieService'

/**
 * offlineCatalogCache.js — OFFLINE-2026-09-28, Phase 1.
 *
 * Standalone, isolated module — CatalogueV3.js only calls the functions
 * exported here, it never reaches into localStorage directly. Nothing in
 * this file changes what the ONLINE path does; it only adds a fallback path
 * for when there's no signal. See memory project_so_v3_offline_catalog_pending.md
 * for the full background, and feedback_reversible_changes_and_online_safety.md
 * for why this stays isolated instead of touching salesOrderCatalog.ts's
 * existing online query.
 *
 * Mirrors CatalogueV2.js's proven approach (10 min data TTL, cache-first
 * decision) but against V3's CURRENT pricing model (4 tiers + per-tier
 * allowBelowCost), which V2 never had — see the "why not just port V2"
 * discussion in that same memory file.
 */

const CATALOG_TTL_MS = 10 * 60 * 1000 // 10 minutes — same as V2's OFFLINE_CACHE_DURATION_MS
const CUSTOMERS_TTL_MS = 30 * 60 * 1000 // customer list/price tiers change far less often than the catalog

function catalogKey(idCompany) {
    return `offlineCatalog:v1:${idCompany}`
}
function catalogTsKey(idCompany) {
    return `offlineCatalog:v1:${idCompany}:ts`
}
function customersKey(idCompany, salespersonNo) {
    return `offlineCustomers:v1:${idCompany}:${salespersonNo || 'all'}`
}
function customersTsKey(idCompany, salespersonNo) {
    return `offlineCustomers:v1:${idCompany}:${salespersonNo || 'all'}:ts`
}

function isExpired(tsKey, ttlMs) {
    const ts = localStorage.getItem(tsKey)
    if (!ts) return true
    const last = parseInt(ts, 10)
    if (isNaN(last)) return true
    return (Date.now() - last) > ttlMs
}

function readJson(key) {
    try {
        const raw = localStorage.getItem(key)
        return raw ? JSON.parse(raw) : null
    } catch (e) {
        return null
    }
}

// QUOTA-2026-11-03 — localStorage is ~5MB per origin and the SO autosave ('SalesOrderSaveV3') lives in the same
// space. The offline copies (catalog x 4 price tiers + customers, per company/salesperson) used to fill it, so
// the autosave threw QuotaExceededError and took the whole SO V3 page down (client/dev report). The ONLINE
// flow always wins: a cache write is only kept if enough room is left for the autosave; otherwise it is
// dropped (and reported as not synced) instead of starving the order. Revert = restore the previous writeJson
// (backup: /private/tmp/offlineCatalogCache.js.bak-2026-11-03).
const HEADROOM_CHARS = 400 * 1024 // room that must stay free after a cache write (autosave of a big order)
const OFFLINE_PREFIXES = ['offlineCatalog:', 'offlineCustomers:']

function hasHeadroom() {
    const probe = '__offline_headroom_probe__'
    try {
        localStorage.setItem(probe, 'x'.repeat(HEADROOM_CHARS))
        localStorage.removeItem(probe)
        return true
    } catch (e) {
        try { localStorage.removeItem(probe) } catch (e2) { /* nothing to clean */ }
        return false
    }
}

// Removes offline copies other than `keepKeys` (other salespeople / other company). Returns how many were removed.
export function evictOfflineCaches(keepKeys) {
    const keep = new Set((keepKeys || []).flatMap((k) => [k, k + ':ts']))
    let removed = 0
    try {
        Object.keys(localStorage).forEach((k) => {
            if (OFFLINE_PREFIXES.some((p) => k.indexOf(p) === 0) && !keep.has(k)) {
                localStorage.removeItem(k)
                removed++
            }
        })
    } catch (e) {
        console.error('offlineCatalogCache: evict failed', e)
    }
    return removed
}

function writeJson(key, tsKey, value) {
    const json = JSON.stringify(value)
    const save = () => {
        localStorage.setItem(key, json)
        localStorage.setItem(tsKey, String(Date.now()))
    }
    try {
        try {
            save()
        } catch (e) {
            // full: drop other users'/companies' copies and retry once
            evictOfflineCaches([key])
            save()
        }
        // no margin left for the order autosave: first free other users'/companies' copies, then re-check
        if (!hasHeadroom()) evictOfflineCaches([key])
        if (!hasHeadroom()) {
            localStorage.removeItem(key)
            localStorage.removeItem(tsKey)
            console.warn('offlineCatalogCache: not enough local storage left; offline copy skipped so the order autosave keeps working', key)
            return false
        }
        return true
    } catch (e) {
        console.error('offlineCatalogCache: failed to save', key, e)
        try { localStorage.removeItem(key); localStorage.removeItem(tsKey) } catch (e2) { /* nothing to clean */ }
        return false
    }
}

export function isOnline() {
    return typeof navigator !== 'undefined' && navigator.onLine !== false
}

// Non-blocking — call while online to keep the offline copy warm. Safe to
// call often: it no-ops if the cache is still fresh, unless force is true.
// Returns a plain status object (never throws) so the caller can show a
// visible confirmation — 2026-09-29, client-reported: a silent background
// save left no way to tell it was actually working. { synced, count } when
// it really fetched+saved; { synced:false, reason } when it no-opped
// (offline, still fresh) or failed (reason:'error').
export async function prefetchOfflineCatalog(idCompany, force) {
    if (!isOnline()) return { synced: false, reason: 'offline' }
    if (!force && !isExpired(catalogTsKey(idCompany), CATALOG_TTL_MS)) return { synced: false, reason: 'fresh' }
    try {
        const resp = await getInformationWithDataV3('/v3/salesorder/offline-catalog', { idCompany })
        if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
            if (!writeJson(catalogKey(idCompany), catalogTsKey(idCompany), resp.data)) return { synced: false, reason: 'storage-full' }
            return { synced: true, count: resp.data.length }
        }
        return { synced: false, reason: 'error' }
    } catch (e) {
        console.error('prefetchOfflineCatalog failed (non-blocking):', e)
        return { synced: false, reason: 'error' }
    }
}

export async function prefetchOfflineCustomers(idCompany, salespersonNo, idTypeofUser, force) {
    if (!isOnline()) return { synced: false, reason: 'offline' }
    const key = customersKey(idCompany, salespersonNo)
    const tsKey = customersTsKey(idCompany, salespersonNo)
    if (!force && !isExpired(tsKey, CUSTOMERS_TTL_MS)) return { synced: false, reason: 'fresh' }
    try {
        const resp = await getInformationWithDataV3('/v3/salesorder/offline-customers', { idCompany, salespersonNo, idTypeofUser })
        if (resp && resp.status && resp.status.code === 1 && Array.isArray(resp.data)) {
            if (!writeJson(key, tsKey, resp.data)) return { synced: false, reason: 'storage-full' }
            return { synced: true, count: resp.data.length }
        }
        return { synced: false, reason: 'error' }
    } catch (e) {
        console.error('prefetchOfflineCustomers failed (non-blocking):', e)
        return { synced: false, reason: 'error' }
    }
}

export function getCachedCatalog(idCompany) {
    return readJson(catalogKey(idCompany)) || []
}

export function getCachedCustomers(idCompany, salespersonNo) {
    return readJson(customersKey(idCompany, salespersonNo)) || []
}

export function hasCachedCatalog(idCompany) {
    return getCachedCatalog(idCompany).length > 0
}

// Same arithmetic as salesOrderCatalog.ts's buildCatalogSql priceSelect/wrap
// (backend) — MUST stay in sync with that file, or online and offline prices
// would silently disagree. priceLevel is a number 1-4 (default 1, same
// fallback the backend uses via COALESCE(cpl.priceLevel, 1)).
export function resolveOfflinePrice(rawItem, priceLevel) {
    const level = [1, 2, 3, 4].includes(Number(priceLevel)) ? Number(priceLevel) : 1
    const levelPrice = rawItem['level' + level + 'Price']
    const standardPrice = (levelPrice !== null && levelPrice !== undefined) ? levelPrice : rawItem.standardPrice
    const allowBelowCost = !!Number(rawItem['allowLevel' + level + 'BelowCost'])
    const cost = rawItem.cost !== null && rawItem.cost !== undefined ? Number(rawItem.cost) : null
    const minPrice = allowBelowCost && cost !== null
        ? Number((cost / 0.95).toFixed(2))
        : rawItem.normalMinPrice

    return {
        ...rawItem,
        standardPrice,
        resolvedPriceLevel: level,
        allowBelowCost,
        minPrice,
    }
}

// Client-side search + product-line filter + pagination over the cached
// catalog — mirrors what /salesorder/catalog does server-side for the
// online path, so CatalogueV3.js's rendering code doesn't need to know
// whether it's looking at an online or offline result set.
export function searchOfflineCatalog(idCompany, priceLevel, { search, productLine, page, pageSize }) {
    const all = getCachedCatalog(idCompany)
    const term = (search || '').trim().toLowerCase()
    const line = productLine || ''

    let filtered = all
    if (term) {
        filtered = filtered.filter((item) => {
            const code = (item.itemCode || '').toLowerCase()
            const desc = (item.description || '').toLowerCase()
            const longDesc = (item.longDescription || '').toLowerCase()
            return code.indexOf(term) !== -1 || desc.indexOf(term) !== -1 || longDesc.indexOf(term) !== -1
        })
    }
    if (line) {
        filtered = filtered.filter((item) => String(item.productLine) === String(line))
    }

    const total = filtered.length
    const safePage = page > 0 ? page : 1
    const safePageSize = pageSize > 0 ? pageSize : 30
    const start = (safePage - 1) * safePageSize
    const pageItems = filtered.slice(start, start + safePageSize)

    const resolved = pageItems.map((item) => resolveOfflinePrice(item, priceLevel))
    return { data: resolved, total }
}

// OFFLINE-2026-09-29 — exact-match lookup by itemCode/upc/upc2/caseupc over
// the cached catalog, offline stand-in for salesOrderCatalog.ts's getByCode
// (same match rule: pp.itemCode = code OR ih.upc/upc2/caseupc = code). Used
// by SearchUPCV3.js's scan-and-add when there's no signal, or the live call
// fails/times out mid-scan. Resolves the customer's price tier the same way
// CatalogueV3.js's offlineCustomerPriceLevel() does (cached customer list,
// default level 1), so a scanned item is priced exactly like a tapped one.
export function findOfflineItemByCode(idCompany, code, customerNo, salespersonNo) {
    const catalog = getCachedCatalog(idCompany)
    const match = catalog.find((item) =>
        item.itemCode === code || item.upc === code || item.upc2 === code || item.caseupc === code
    )
    if (!match) return null

    const customers = getCachedCustomers(idCompany, salespersonNo)
    const customerMatch = customers.find((c) => c.CustomerNo === customerNo)
    const priceLevel = (customerMatch && customerMatch.priceLevel) ? Number(customerMatch.priceLevel) : 1

    return resolveOfflinePrice(match, priceLevel)
}

// OFFLINE-2026-09-29 — translates one cached AR_Customer row (raw Sage-style
// column names, from getFullCustomerList) into the EXACT camelCase shape
// CustomerListV3.js already expects from the live /v3/salesorder/customer-list
// response (customerNo, name, addressLine1, ...) — so CustomerListV3.js's own
// render()/selectCustomer() code needs zero branching on where the row came
// from. Keep in sync with salesOrderCustomer.ts's getList() column aliases.
function mapOfflineCustomerToRow(c) {
    return {
        customerNo: c.CustomerNo,
        name: c.CustomerName,
        addressLine1: c.AddressLine1,
        addressLine2: c.AddressLine2 || '',
        city: c.City,
        state: c.State,
        zipCode: c.ZipCode,
        phone1: c.TelephoneNo,
        email: c.EmailAddress,
        termsCode: c.TermsCode || '',
        creditLimit: c.CreditLimit,
        priceLevel: c.priceLevel,
    }
}

// Client-side search + pagination over the cached customer list — offline
// stand-in for salesOrderCustomer.ts's getList (same search fields: CustomerNo/
// CustomerName/AddressLine1/City/TelephoneNo). Used by CustomerListV3.js when
// there's no signal, or the live call fails.
export function searchOfflineCustomers(idCompany, salespersonNo, { search, page, pageSize }) {
    const all = getCachedCustomers(idCompany, salespersonNo)
    const term = (search || '').trim().toLowerCase()

    let filtered = all
    if (term) {
        filtered = filtered.filter((c) => {
            const no = (c.CustomerNo || '').toLowerCase()
            const name = (c.CustomerName || '').toLowerCase()
            const addr = (c.AddressLine1 || '').toLowerCase()
            const city = (c.City || '').toLowerCase()
            const phone = (c.TelephoneNo || '').toLowerCase()
            return no.indexOf(term) !== -1 || name.indexOf(term) !== -1 || addr.indexOf(term) !== -1
                || city.indexOf(term) !== -1 || phone.indexOf(term) !== -1
        })
    }

    const total = filtered.length
    const safePage = page > 0 ? page : 1
    const safePageSize = pageSize > 0 ? pageSize : 50
    const start = (safePage - 1) * safePageSize
    const pageItems = filtered.slice(start, start + safePageSize)

    return { data: pageItems.map(mapOfflineCustomerToRow), total }
}

// Distinct product lines from the cached catalog — offline stand-in for
// /v3/salesorder/product-lines.
export function getOfflineProductLines(idCompany) {
    const all = getCachedCatalog(idCompany)
    const seen = {}
    const lines = []
    all.forEach((item) => {
        const code = item.productLine
        if (!code || seen[code]) return
        seen[code] = true
        lines.push({ ProductLine: code, ProductLineDesc: item.productLineDesc })
    })
    return lines
}
